import type { ChatMessage, ChatMessagePart, StreamEvent } from './types';
import { getCsrfToken } from '@/lib/csrf';
import {
    adaptStreamEvent,
    createAdapterState,
    isTerminalEvent as adapterIsTerminal,
} from './adapters/chat-stream-adapter';

export async function* streamChat(
    payload: {
        conversationId: string;
        capabilityRef: string;
        method?: string;
        messages: Array<{ id: string; role: string; parts: ChatMessagePart[] }>;
    },
    signal?: AbortSignal,
): AsyncGenerator<StreamEvent> {
    const csrfToken = getCsrfToken();
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
    };
    if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
    }

    const res = await fetch('/api/chat/stream', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload),
        signal,
    });

    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
    }

    const reader = res.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEventType = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
            if (line.startsWith('event: ')) {
                currentEventType = line.slice(7).trim();
                continue;
            }

            if (line.startsWith('data: ')) {
                const dataStr = line.slice(6).trim();
                if (dataStr === '[DONE]') return;

                try {
                    const event: StreamEvent = JSON.parse(dataStr);
                    if (!event.type && currentEventType) {
                        event.type = currentEventType;
                    }
                    yield event;
                } catch {
                    // skip unparseable
                }
                currentEventType = '';
            }
        }
    }
}

/**
 * Applies a stream event to build up assistant message parts.
 *
 * The admin-ui Go BFF proxies the HTTP gateway's SSE stream. The gateway
 * wraps each StreamEvent inside a StreamChunk, which means:
 *   - `itemId` lives only in data (not at top level)
 *   - Sync-fallback responses arrive as type "result" (not "run.completed")
 *   - Gateway errors arrive as type "error"
 *   - A synthesized "completed" chunk may appear if the stream ends without
 *     a terminal event from the worker.
 */
export function applyStreamEvent(
    event: StreamEvent,
    state: StreamReducerState,
): ChatMessagePart[] {
    return adaptStreamEvent(event, state as any);
}

export interface ToolState {
    toolCallId: string;
    toolName: string;
    args: Record<string, unknown>;
    status: string;
}

export interface StreamReducerState {
    textAccumulator: string;
    toolStates: Map<string, ToolState>;
    parts: ChatMessagePart[];
    activeItems: Map<string, { kind: string; partIndex: number }>;
}

export function createStreamReducerState(): StreamReducerState {
    return createAdapterState();
}

export function isTerminalEvent(event: StreamEvent): boolean {
    return adapterIsTerminal(event);
}
